import React, { useState, useEffect, useRef } from "react";
import { QRCodeSVG } from "qrcode.react";
import Card from "components/card";
import { MdEdit, MdDelete, MdVisibility, MdQrCode2, MdClose, MdDownload, MdSearch, MdError } from "react-icons/md";
import ViewRecord from "./ViewRecord";
import EditWarehouseModal from "./EditWarehouse";
import { useLocation } from 'react-router-dom';
import html2canvas from 'html2canvas';
import { Toaster, toast } from 'react-hot-toast';
import axios from 'axios';

const Loader = () => (
  <div className="flex h-[400px] items-center justify-center">
    <div className="relative">
      {/* Outer ring */}
      <div className="h-24 w-24 rounded-full border-t-4 border-b-4 border-brand-500"></div>
      {/* Inner ring */}
      <div className="absolute top-0 left-0 h-24 w-24 animate-spin rounded-full border-t-4 border-b-4 border-gray-200"></div>
      {/* Center dot */}
      <div className="absolute top-1/2 left-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-500"></div>
      {/* Loading text */}
      <div className="mt-8 text-center">
        <p className="text-lg font-medium text-gray-600 dark:text-white">Loading</p>
        <p className="text-sm text-gray-400">Please wait...</p>
      </div>
    </div>
  </div>
);

const ViewWarehouse = () => {
  const location = useLocation();
  const scannedId = location.state?.selectedRecordId;
  const [warehouseData, setWarehouseData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [qrModalData, setQrModalData] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [printMode, setPrintMode] = useState('single');
  const printRef = useRef(null);
  const [selectAll, setSelectAll] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [editingRecord, setEditingRecord] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  // Calculate pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = warehouseData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(warehouseData.length / itemsPerPage);

  // Add search filter function
  const filteredData = warehouseData.filter((item) =>
    Object.values(item).some((value) =>
      String(value).toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  // Add fetchData function
  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://usibras-web-backend.onrender.com/api/fetchcalibratedRCN');
      const result = await response.json();
      
      if (result.data) {
        // Transform the data to match your existing structure
        const transformedData = result.data.map(item => ({
          id: item.custom_id,
          date: item.date,
          truckNumber: item.truck_number,
          origin: item.origin,
          weighingBridgeWeight: parseFloat(item.weighing_bridge_weight),
          numberOfBags: item.number_of_bags,
          entryMoisture: parseFloat(item.entry_moisture),
          calibrationMoisture: parseFloat(item.calibration_moisture),
          dustOffloaded: parseFloat(item.dust_offloaded),
          dustAfterDrying: parseFloat(item.dust_after_drying),
          actualCalibratedBags: item.actual_calibrated_bags,
          actualCalibratedKg: parseFloat(item.actual_calibrated_kg),
          moistureToAutoclave: parseFloat(item.moisture_to_autoclave),
          pricePerKg: parseFloat(item.price_per_kg),
          createdAt: item.created_at,
          updatedAt: item.updated_at
        }));
        
        setWarehouseData(transformedData);
      }
    } catch (err) {
      setError('Failed to fetch data');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Add useEffect to fetch data on component mount
  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (scannedId) {
      // Find the record with the scanned ID
      const record = warehouseData.find(item => item.id === scannedId);
      if (record) {
        // Show the record details
        handleView(scannedId);
      } else {
        // Handle case when record is not found
        toast.error("Record not found");
      }
    }
  }, [scannedId]);

  const handleEdit = (id) => {
    const recordToEdit = warehouseData.find(item => item.id === id);
    setEditingRecord(recordToEdit);
  };

  const handleDelete = async (id) => {
    // Show confirmation dialog
    if (window.confirm('Are you sure you want to delete this record? This action cannot be undone.')) {
      setDeletingId(id); // Set the ID of the record being deleted
      
      try {
        const response = await axios.delete(`https://usibras-web-backend.onrender.com/api/deletecalibratedRCN/${id}`);
        
        if (response.data.success) {
          setWarehouseData(prevData => prevData.filter(item => item.id !== id));
          toast.success(response.data.message);
        } else {
          toast.error(response.data.message || 'Failed to delete record');
        }
      } catch (error) {
        console.error('Error deleting record:', error);
        toast.error(error.response?.data?.message || 'Failed to delete record');
      } finally {
        setDeletingId(null); // Clear the deleting state
      }
    }
  };

  const handleView = (id) => {
    const record = warehouseData.find(item => item.id === id);
    setSelectedRecord(record);
  };

  const handleGenerateQR = (id) => {
    const record = warehouseData.find(item => item.id === id);
    setQrModalData(record);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Approved": return "text-green-500";
      case "Pending": return "text-yellow-500";
      case "Rejected": return "text-red-500";
      default: return "text-gray-500";
    }
  };

  // Function to download single QR code
  const downloadQR = async (elementRef, filename) => {
    const canvas = await html2canvas(elementRef.current);
    const data = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.href = data;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Function to download multiple QR codes
  const downloadBulkQR = async (elementRef, selectedData) => {
    const canvas = await html2canvas(elementRef.current);
    const data = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.href = data;
    link.download = `qr_codes_${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Add checkbox selection for records
  const handleSelectRecord = (id) => {
    setSelectedRecords(prev => 
      prev.includes(id) 
        ? prev.filter(recordId => recordId !== id)
        : [...prev, id]
    );
  };

  // Handle select all checkbox
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedRecords([]);
    } else {
      const allIds = warehouseData.map(item => item.id);
      setSelectedRecords(allIds);
    }
    setSelectAll(!selectAll);
  };

  // Change page
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // QR Code Modal Component with Download Options
  const QRModal = ({ data, onClose }) => {
    const singleQrRef = useRef(null);
    const bulkQrRef = useRef(null);
    const selectedData = warehouseData.filter(item => selectedRecords.includes(item.id));
    
    return (
      <>
        <div 
          className="fixed inset-0 z-40 backdrop-blur-sm bg-black/30"
          onClick={onClose}
        />
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto p-4">
          <div className="relative w-[800px] rounded-xl bg-white p-6 shadow-xl dark:bg-navy-800">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-bold text-navy-700 dark:text-white">
                QR Code Generator
              </h3>
              <button
                onClick={onClose}
                className="rounded-full bg-lightPrimary p-2 text-brand-500 hover:bg-gray-100 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-600"
              >
                <MdClose className="h-5 w-5" />
              </button>
            </div>

            {/* Mode Selection */}
            <div className="mb-4 flex items-center gap-4">
              <button
                onClick={() => setPrintMode('single')}
                className={`px-4 py-2 rounded-lg ${
                  printMode === 'single' 
                    ? 'bg-brand-500 text-white' 
                    : 'bg-gray-100 text-gray-700'
                }`}
              >
                Single QR
              </button>
              <button
                onClick={() => setPrintMode('bulk')}
                className={`px-4 py-2 rounded-lg ${
                  printMode === 'bulk' 
                    ? 'bg-brand-500 text-white' 
                    : 'bg-gray-100 text-gray-700'
                }`}
              >
                Bulk QR ({selectedRecords.length} selected)
              </button>
            </div>

            {/* Preview Area */}
            {printMode === 'single' ? (
              <div ref={singleQrRef} className="bg-white p-4">
                <div className="flex flex-col items-center gap-4">
                  <QRCodeSVG
                    value={data.id}
                    size={200}
                    level="H"
                    includeMargin={true}
                    className="bg-white p-2 rounded-lg"
                  />
                  <div className="text-center">
                    <p className="font-semibold text-navy-700">
                      ID: {data.id}
                    </p>
                    <p className="text-sm text-gray-600">
                      {data.origin}
                    </p>
                    <p className="text-xs text-gray-500">
                      Date: {new Date(data.date).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div ref={bulkQrRef} className="bg-white p-4">
                <div className="grid grid-cols-2 gap-4">
                  {selectedData.map((item) => (
                    <div key={item.id} className="flex flex-col items-center p-2 border rounded">
                      <QRCodeSVG
                        value={item.id}
                        size={150}
                        level="H"
                        includeMargin={true}
                        className="bg-white p-1 rounded-lg"
                      />
                      <div className="text-center mt-2">
                        <p className="text-sm font-semibold text-navy-700">
                          ID: {item.id}
                        </p>
                        <p className="text-xs text-gray-600">
                          {item.origin}
                        </p>
                        <p className="text-xs text-gray-500">
                          Date: {new Date(item.date).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Download Button */}
            <button
              onClick={() => {
                if (printMode === 'single') {
                  downloadQR(singleQrRef, `qr_code_${data.id}.png`);
                } else {
                  downloadBulkQR(bulkQrRef, selectedData);
                }
              }}
              className="mt-4 w-full flex items-center justify-center gap-2 bg-brand-500 text-white py-2 rounded-lg hover:bg-brand-600"
            >
              <MdDownload className="h-5 w-5" />
              Download QR Code{printMode === 'bulk' ? 's' : ''}
            </button>
          </div>
        </div>
      </>
    );
  };

  // Add handleUpdate function
  const handleUpdate = (updatedData) => {
    setWarehouseData(prevData =>
      prevData.map(item =>
        item.id === updatedData.id ? updatedData : item
      )
    );
    setEditingRecord(null);
  };

  const columns = [
    {
      Header: "DATE",
      accessor: "date",
    },
    {
      Header: "TRUCK NUMBER",
      accessor: "truckNumber",
    },
    {
      Header: "ORIGIN",
      accessor: "origin",
    },
    {
      Header: "WEIGHING BRIDGE WEIGHT",
      accessor: "weighingBridgeWeight",
    },
    {
      Header: "NUMBER OF BAGS",
      accessor: "numberOfBags",
    },
    {
      Header: "ENTRY MOISTURE %",
      accessor: "entryMoisture",
    },
    {
      Header: "CALIBRATION MOISTURE %",
      accessor: "calibrationMoisture",
    },
    {
      Header: "DUST OFFLOADED",
      accessor: "dustOffloaded",
    },
    {
      Header: "DUST AFTER DRYING",
      accessor: "dustAfterDrying",
    },
    {
      Header: "ACTUAL CALIBRATED BAGS",
      accessor: "actualCalibratedBags",
    },
    {
      Header: "ACTUAL CALIBRATED KG",
      accessor: "actualCalibratedKg",
    },
    {
      Header: "MOISTURE TO AUTOCLAVE %",
      accessor: "moistureToAutoclave",
    },
    {
      Header: "PRICE PER KG (GHS)",
      accessor: "pricePerKg",
    },
    // {
    //   Header: "COST OF CASHEW (GHS)",
    //   accessor: "costOfCashew",
    // },
    {
      Header: "ACTIONS",
      accessor: "actions",
    },
  ];

  if (selectedRecord) {
    return <ViewRecord record={selectedRecord} onClose={() => setSelectedRecord(null)} />;
  }

  return (
    <div className="mt-5 h-full">
      <Toaster position="top-right" />
      <Card extra={"w-full h-full p-4"}>
        {loading ? (
          <Loader />
        ) : error ? (
          <div className="flex h-[400px] items-center justify-center">
            <div className="text-center">
              <div className="mb-4 text-5xl text-red-500">
                <MdError className="mx-auto h-16 w-16" />
              </div>
              <p className="text-xl font-semibold text-gray-700 dark:text-white">
                Oops! Something went wrong
              </p>
              <p className="mt-2 text-gray-500 dark:text-gray-400">{error}</p>
              <button
                onClick={fetchData}
                className="mt-4 rounded-lg bg-brand-500 px-4 py-2 text-white hover:bg-brand-600"
              >
                Try Again
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Add search bar */}
            <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-2">
                <h4 className="text-xl font-bold text-navy-700 dark:text-white">
                  Calibrated RCN Data
                </h4>
                <span className="rounded-full bg-brand-500 px-2 py-1 text-xs text-white">
                  {filteredData.length} records
                </span>
              </div>
              <div className="flex w-full flex-col gap-4 md:w-[380px] md:flex-row">
                <div className="flex w-full items-center rounded-lg border bg-white px-3 py-2 shadow-md dark:bg-navy-700">
                  <MdSearch className="h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by ID, Origin, Status..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="ml-2 w-full bg-transparent text-sm font-medium text-gray-600 outline-none dark:text-white"
                  />
                </div>
              </div>
            </div>

            <div className="relative flex items-center justify-between pt-4">
              {/* <div className="text-xl font-bold text-navy-700 dark:text-white">
                Warehouse Records
              </div> */}
              {selectedRecords.length > 0 && (
                <button
                  onClick={() => handleGenerateQR(selectedRecords[0])}
                  className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-white hover:bg-brand-600"
                >
                  <MdQrCode2 className="h-5 w-5" />
                  Generate Bulk QR ({selectedRecords.length})
                </button>
              )}
            </div>

            <div className="mt-8 w-full overflow-x-auto">
              <div className="min-w-max">
                <table className="w-full">
                  <thead>
                    <tr className="!border-px !border-gray-400">
                      <th className="cursor-pointer border-b-[1px] border-gray-200 pt-4 pb-2 pr-4 text-start">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={selectAll}
                            onChange={handleSelectAll}
                            className="h-4 w-4 rounded border-gray-300 cursor-pointer"
                          />
                        </div>
                      </th>
                      <th className="cursor-pointer border-b-[1px] border-gray-200 pt-4 pb-2 pr-4 text-start">
                        <p className="text-sm font-bold text-gray-600 dark:text-white">ID</p>
                      </th>
                      <th className="cursor-pointer border-b-[1px] border-gray-200 pt-4 pb-2 pr-4 text-start">
                        <p className="text-sm font-bold text-gray-600 dark:text-white">Date</p>
                      </th>
                      <th className="cursor-pointer border-b-[1px] border-gray-200 pt-4 pb-2 pr-4 text-start">
                        <p className="text-sm font-bold text-gray-600 dark:text-white">Truck Number</p>
                      </th>
                      <th className="cursor-pointer border-b-[1px] border-gray-200 pt-4 pb-2 pr-4 text-start">
                        <p className="text-sm font-bold text-gray-600 dark:text-white">Origin</p>
                      </th>
                      <th className="cursor-pointer border-b-[1px] border-gray-200 pt-4 pb-2 pr-4 text-start">
                        <p className="text-sm font-bold text-gray-600 dark:text-white">Weighing Bridge Weight</p>
                      </th>
                      <th className="cursor-pointer border-b-[1px] border-gray-200 pt-4 pb-2 pr-4 text-start">
                        <p className="text-sm font-bold text-gray-600 dark:text-white">Number of Bags</p>
                      </th>
                      <th className="cursor-pointer border-b-[1px] border-gray-200 pt-4 pb-2 pr-4 text-start">
                        <p className="text-sm font-bold text-gray-600 dark:text-white">Entry Moisture %</p>
                      </th>
                      <th className="cursor-pointer border-b-[1px] border-gray-200 pt-4 pb-2 pr-4 text-start">
                        <p className="text-sm font-bold text-gray-600 dark:text-white">Calibration Moisture %</p>
                      </th>
                      <th className="cursor-pointer border-b-[1px] border-gray-200 pt-4 pb-2 pr-4 text-start">
                        <p className="text-sm font-bold text-gray-600 dark:text-white">Dust Offloaded</p>
                      </th>
                      <th className="cursor-pointer border-b-[1px] border-gray-200 pt-4 pb-2 pr-4 text-start">
                        <p className="text-sm font-bold text-gray-600 dark:text-white">Dust After Drying</p>
                      </th>
                      <th className="cursor-pointer border-b-[1px] border-gray-200 pt-4 pb-2 pr-4 text-start">
                        <p className="text-sm font-bold text-gray-600 dark:text-white">Actual Calibrated Bags</p>
                      </th>
                      <th className="cursor-pointer border-b-[1px] border-gray-200 pt-4 pb-2 pr-4 text-start">
                        <p className="text-sm font-bold text-gray-600 dark:text-white">Actual Calibrated KG</p>
                      </th>
                      <th className="cursor-pointer border-b-[1px] border-gray-200 pt-4 pb-2 pr-4 text-start">
                        <p className="text-sm font-bold text-gray-600 dark:text-white">Moisture to Autoclave %</p>
                      </th>
                      <th className="cursor-pointer border-b-[1px] border-gray-200 pt-4 pb-2 pr-4 text-start">
                        <p className="text-sm font-bold text-gray-600 dark:text-white">Price per Kg (GHS)</p>
                      </th>
                      {/* <th className="cursor-pointer border-b-[1px] border-gray-200 pt-4 pb-2 pr-4 text-start">
                        <p className="text-sm font-bold text-gray-600 dark:text-white">Cost of Cashew (GHS)</p>
                      </th> */}
                      <th className="cursor-pointer border-b-[1px] border-gray-200 pt-4 pb-2 pr-4 text-start">
                        <p className="text-sm font-bold text-gray-600 dark:text-white">Actions</p>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.map((item) => (
                      <tr key={item.id}>
                        <td className="min-w-[50px] border-b py-4 pr-4">
                          <input
                            type="checkbox"
                            checked={selectedRecords.includes(item.id)}
                            onChange={() => handleSelectRecord(item.id)}
                            className="h-4 w-4 rounded border-gray-300 cursor-pointer"
                          />
                        </td>
                        <td className="min-w-[120px] border-b py-4 pr-4">
                          <p className="text-sm font-medium text-navy-700 dark:text-white">{item.id}</p>
                        </td>
                        <td className="min-w-[110px] border-b py-4 pr-4">
                          <p className="text-sm font-medium text-navy-700 dark:text-white">
                            {new Date(item.date).toLocaleDateString()}
                          </p>
                        </td>
                        <td className="min-w-[120px] border-b py-4 pr-4">
                          <p className="text-sm font-medium text-navy-700 dark:text-white">{item.truckNumber}</p>
                        </td>
                        <td className="min-w-[120px] border-b py-4 pr-4">
                          <p className="text-sm font-medium text-navy-700 dark:text-white">{item.origin}</p>
                        </td>
                        <td className="min-w-[120px] border-b py-4 pr-4">
                          <p className="text-sm font-medium text-navy-700 dark:text-white">{item.weighingBridgeWeight}</p>
                        </td>
                        <td className="min-w-[120px] border-b py-4 pr-4">
                          <p className="text-sm font-medium text-navy-700 dark:text-white">{item.numberOfBags}</p>
                        </td>
                        <td className="min-w-[120px] border-b py-4 pr-4">
                          <p className="text-sm font-medium text-navy-700 dark:text-white">{item.entryMoisture}%</p>
                        </td>
                        <td className="min-w-[120px] border-b py-4 pr-4">
                          <p className="text-sm font-medium text-navy-700 dark:text-white">{item.calibrationMoisture}%</p>
                        </td>
                        <td className="min-w-[120px] border-b py-4 pr-4">
                          <p className="text-sm font-medium text-navy-700 dark:text-white">{item.dustOffloaded}</p>
                        </td>
                        <td className="min-w-[120px] border-b py-4 pr-4">
                          <p className="text-sm font-medium text-navy-700 dark:text-white">{item.dustAfterDrying}</p>
                        </td>
                        <td className="min-w-[120px] border-b py-4 pr-4">
                          <p className="text-sm font-medium text-navy-700 dark:text-white">{item.actualCalibratedBags}</p>
                        </td>
                        <td className="min-w-[120px] border-b py-4 pr-4">
                          <p className="text-sm font-medium text-navy-700 dark:text-white">{item.actualCalibratedKg}</p>
                        </td>
                        <td className="min-w-[120px] border-b py-4 pr-4">
                          <p className="text-sm font-medium text-navy-700 dark:text-white">{item.moistureToAutoclave}%</p>
                        </td>
                        <td className="min-w-[120px] border-b py-4 pr-4">
                          <p className="text-sm font-medium text-navy-700 dark:text-white">{item.pricePerKg.toFixed(2)}</p>
                        </td>
                        {/* <td className="min-w-[120px] border-b py-4 pr-4">
                          <p className="text-sm font-medium text-navy-700 dark:text-white">{item.costOfCashew.toFixed(2)}</p>
                        </td> */}
                        <td className="min-w-[180px] border-b py-4 pr-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleView(item.id)}
                              className="flex items-center justify-center rounded-lg bg-lightPrimary p-2 text-brand-500 transition duration-200 hover:bg-gray-100 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-800"
                            >
                              <MdVisibility className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => handleEdit(item.id)}
                              className="flex items-center justify-center rounded-lg bg-lightPrimary p-2 text-brand-500 transition duration-200 hover:bg-gray-100 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-800"
                            >
                              <MdEdit className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => handleDelete(item.id)}
                              disabled={deletingId === item.id}
                              className={`flex items-center justify-center rounded-lg bg-lightPrimary p-2 text-red-500 transition duration-200 hover:bg-gray-100 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-800 ${
                                deletingId === item.id ? 'opacity-50 cursor-not-allowed' : ''
                              }`}
                            >
                              <MdDelete className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => handleGenerateQR(item.id)}
                              className="flex items-center justify-center rounded-lg bg-lightPrimary p-2 text-brand-500 transition duration-200 hover:bg-gray-100 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-800"
                            >
                              <MdQrCode2 className="h-5 w-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Show message when no results found */}
            {filteredData.length === 0 && (
              <div className="mt-4 flex h-[200px] items-center justify-center rounded-lg border border-gray-200 bg-gray-50 dark:border-navy-700 dark:bg-navy-800">
                <p className="text-center text-gray-500 dark:text-gray-400">
                  No records found matching your search
                </p>
              </div>
            )}

            {/* Pagination */}
            <div className="mt-4 flex items-center justify-between px-4">
              <div className="text-sm text-gray-600 dark:text-white">
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, warehouseData.length)} of {warehouseData.length} entries
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`rounded-lg px-3 py-1 text-sm ${
                    currentPage === 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-brand-500 text-white hover:bg-brand-600'
                  }`}
                >
                  Previous
                </button>
                {[...Array(totalPages)].map((_, index) => (
                  <button
                    key={index + 1}
                    onClick={() => handlePageChange(index + 1)}
                    className={`rounded-lg px-3 py-1 text-sm ${
                      currentPage === index + 1
                        ? 'bg-brand-500 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-600'
                    }`}
                  >
                    {index + 1}
                  </button>
                ))}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`rounded-lg px-3 py-1 text-sm ${
                    currentPage === totalPages
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-brand-500 text-white hover:bg-brand-600'
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </Card>
      
      {qrModalData && (
        <QRModal 
          data={qrModalData} 
          onClose={() => setQrModalData(null)} 
        />
      )}

      {/* Add EditWarehouse component */}
      {editingRecord && (
        <EditWarehouseModal 
          record={editingRecord}
          onClose={() => setEditingRecord(null)}
          onUpdate={handleUpdate}
        />
      )}

      {/* Loading Overlay */}
      {deletingId && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/80 dark:bg-navy-700/80">
          <div className="relative">
            <div className="h-24 w-24 rounded-full border-t-4 border-b-4 border-brand-500"></div>
            <div className="absolute top-0 left-0 h-24 w-24 animate-spin rounded-full border-t-4 border-b-4 border-gray-200"></div>
            <div className="absolute top-1/2 left-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-500"></div>
            <div className="mt-8 text-center">
              <p className="text-lg font-medium text-gray-600 dark:text-white">Deleting</p>
              <p className="text-sm text-gray-400">Please wait...</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewWarehouse; 